import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, Container } from '@mui/material';
import { theme } from './theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import Header from './components/Header';
import WelcomeSection from './components/WelcomeSection';
import TeaserCards from './components/TeaserCards';
import PitchDeck from './components/PitchDeck';
import GrowthStrategy from './components/GrowthStrategy';
import MarketingStrategy from './components/MarketingStrategy';
import BusinessPlan from './components/BusinessPlan';
import Comparison from './components/Comparison';
import CorporateCulture from './components/CorporateCulture';
import ScrollToTop from './components/ScrollToTop';
import ContactForm from './components/ContactForm';
import Footer from './components/Footer';
import MouseWaveEffect from './components/MouseWaveEffect';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const MainContent = () => {
  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      backgroundColor: '#ffffff',
      isolation: 'isolate',
      overflowX: 'hidden',
    }}>
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: '#ffffff',
        isolation: 'isolate',
        overflowX: 'hidden',
        '@keyframes floatCircle1': {
          '0%': { transform: 'translate(0, 0)' },
          '50%': { transform: 'translate(20px, -20px)' },
          '100%': { transform: 'translate(0, 0)' }
        },
        '@keyframes floatCircle2': {
          '0%': { transform: 'translate(0, 0)' },
          '50%': { transform: 'translate(-15px, 15px)' },
          '100%': { transform: 'translate(0, 0)' }
        },
        '@keyframes floatCircle3': {
          '0%': { transform: 'translate(0, 0) scale(1)' },
          '50%': { transform: 'translate(10px, -10px) scale(1.1)' },
          '100%': { transform: 'translate(0, 0) scale(1)' }
        },
        '.gradient-grid': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(600px circle at 10% 20%, rgba(63, 160, 255, 0.08) 0%, transparent 70%),
            radial-gradient(800px circle at 90% 40%, rgba(63, 160, 255, 0.08) 0%, transparent 70%),
            radial-gradient(700px circle at 15% 70%, rgba(63, 160, 255, 0.08) 0%, transparent 70%),
            radial-gradient(500px circle at 80% 90%, rgba(63, 160, 255, 0.08) 0%, transparent 70%),
            linear-gradient(45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%),
            linear-gradient(-45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%)
          `,
          backgroundSize: '100% 100%, 100% 100%, 100% 100%, 100% 100%, 30px 30px, 30px 30px',
          backgroundRepeat: 'no-repeat, no-repeat, no-repeat, no-repeat, repeat, repeat',
          backgroundPosition: '0 0, 0 0, 0 0, 0 0, 0 0, 0 0',
          zIndex: -1,
          pointerEvents: 'none'
        },
        '.ring-bottom': {
          content: '""',
          position: 'fixed',
          left: { xs: '-50%', md: '-20%' },
          bottom: { xs: '-30%', md: '-20%' },
          width: { xs: '400px', md: '600px' },
          height: { xs: '400px', md: '600px' },
          borderRadius: '50%',
          transform: 'translate(0, 0) rotate(0deg)',
          transformOrigin: 'center center',
          willChange: 'transform',
          background: `
            radial-gradient(circle at center,
              transparent 35%,
              rgba(63, 160, 255, 0.08) 36%,
              rgba(63, 160, 255, 0.06) 38%,
              rgba(147, 51, 234, 0.06) 40%,
              rgba(147, 51, 234, 0.08) 42%,
              rgba(63, 160, 255, 0.1) 44%,
              rgba(63, 160, 255, 0.08) 46%,
              rgba(147, 51, 234, 0.08) 48%,
              transparent 50%
            ),
            conic-gradient(from 0deg at center,
              rgba(63, 160, 255, 0.05) 0deg,
              rgba(147, 51, 234, 0.05) 90deg,
              rgba(63, 160, 255, 0.05) 180deg,
              rgba(147, 51, 234, 0.05) 270deg,
              rgba(63, 160, 255, 0.05) 360deg
            ),
            repeating-conic-gradient(from 0deg at center,
              rgba(63, 160, 255, 0.02) 0deg,
              rgba(147, 51, 234, 0.02) 20deg,
              rgba(63, 160, 255, 0.02) 40deg
            )
          `,
          animation: 'rotateRing 30s linear infinite',
          zIndex: 0,
          pointerEvents: 'none'
        },
        '.ring-top': {
          content: '""',
          position: 'fixed',
          right: { xs: '-50%', md: '-20%' },
          top: { xs: '-30%', md: '-20%' },
          width: { xs: '400px', md: '600px' },
          height: { xs: '400px', md: '600px' },
          borderRadius: '50%',
          transform: 'translate(0, 0) rotate(0deg)',
          transformOrigin: 'center center',
          willChange: 'transform',
          background: `
            radial-gradient(circle at center,
              transparent 35%,
              rgba(147, 51, 234, 0.08) 36%,
              rgba(147, 51, 234, 0.06) 38%,
              rgba(63, 160, 255, 0.06) 40%,
              rgba(63, 160, 255, 0.08) 42%,
              rgba(147, 51, 234, 0.1) 44%,
              rgba(147, 51, 234, 0.08) 46%,
              rgba(63, 160, 255, 0.08) 48%,
              transparent 50%
            ),
            conic-gradient(from 180deg at center,
              rgba(147, 51, 234, 0.05) 0deg,
              rgba(63, 160, 255, 0.05) 90deg,
              rgba(147, 51, 234, 0.05) 180deg,
              rgba(63, 160, 255, 0.05) 270deg,
              rgba(147, 51, 234, 0.05) 360deg
            ),
            repeating-conic-gradient(from 180deg at center,
              rgba(147, 51, 234, 0.02) 0deg,
              rgba(63, 160, 255, 0.02) 20deg,
              rgba(147, 51, 234, 0.02) 40deg
            )
          `,
          animation: 'rotateRingReverse 30s linear infinite',
          zIndex: 0,
          pointerEvents: 'none'
        },
        '& > div:first-of-type::before': {
          content: '""',
          position: 'fixed',
          inset: 0,
          background: `
            radial-gradient(380px circle at 70% 10%, rgba(63, 160, 255, 0.08) 0%, transparent 70%),
            radial-gradient(420px circle at 10% 40%, rgba(63, 160, 255, 0.06) 0%, transparent 70%),
            radial-gradient(360px circle at 40% 80%, rgba(63, 160, 255, 0.1) 0%, transparent 70%)
          `,
          zIndex: -1,
          pointerEvents: 'none',
          animation: 'floatCircle2 25s ease-in-out infinite'
        },
        '& > div:first-of-type::after': {
          content: '""',
          position: 'fixed',
          inset: 0,
          background: `
            radial-gradient(350px circle at 85% 30%, rgba(63, 160, 255, 0.1) 0%, transparent 70%),
            radial-gradient(330px circle at 15% 60%, rgba(63, 160, 255, 0.08) 0%, transparent 70%),
            radial-gradient(400px circle at 50% 90%, rgba(63, 160, 255, 0.06) 0%, transparent 70%)
          `,
          zIndex: -1,
          pointerEvents: 'none',
          animation: 'floatCircle3 30s ease-in-out infinite'
        },
        '@keyframes pulse': {
          '0%': {
            transform: 'translate(-50%, -50%) scale(1)'
          },
          '50%': {
            transform: 'translate(-50%, -50%) scale(1.2)'
          },
          '100%': {
            transform: 'translate(-50%, -50%) scale(1)'
          }
        },
        '@keyframes rotateRing': {
          '0%': {
            transform: 'translate(0, 0) rotate(0deg) scale(1)'
          },
          '25%': {
            transform: 'translate(20px, -20px) rotate(90deg) scale(1.02)'
          },
          '50%': {
            transform: 'translate(0, -30px) rotate(180deg) scale(1.05)'
          },
          '75%': {
            transform: 'translate(-20px, -20px) rotate(270deg) scale(1.02)'
          },
          '100%': {
            transform: 'translate(0, 0) rotate(360deg) scale(1)'
          }
        },
        '@keyframes rotateRingReverse': {
          '0%': {
            transform: 'translate(0, 0) rotate(360deg) scale(1)'
          },
          '25%': {
            transform: 'translate(-20px, 20px) rotate(270deg) scale(1.02)'
          },
          '50%': {
            transform: 'translate(0, 30px) rotate(180deg) scale(1.05)'
          },
          '75%': {
            transform: 'translate(20px, 20px) rotate(90deg) scale(1.02)'
          },
          '100%': {
            transform: 'translate(0, 0) rotate(0deg) scale(1)'
          }
        }
      }}>
        <MouseWaveEffect />
        <Box className="gradient-grid" />
        <Box className="ring-bottom" />
        <Box className="ring-top" />
        <Box sx={{ position: 'relative', zIndex: 5 }}>
          <Header />
        </Box>
        <Container maxWidth="lg" sx={{ flex: 1, py: 4, position: 'relative', zIndex: 1 }}>
          <Box id="teaser">
            <WelcomeSection />
            <TeaserCards />
            <Box id="next-basket-pitch-deck">
              <PitchDeck />
            </Box>
          </Box>

          <Box id="next-basket-growth-strategy">
            <GrowthStrategy />
          </Box>
          <Box id="next-basket-comparison">
            <Comparison />
          </Box>
          <Box id="next-basket-corporate-culture">
            <CorporateCulture />
          </Box>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <ContactForm />
          </Box>
        </Container>
        <ScrollToTop />
        <Footer />
      </Box>
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={<MainContent />}
            />
            <Route
              path="*"
              element={<Navigate to="/" />}
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
